import * as React from "react"
import {
  DirectionFromOrigin,
  type ListWorkItemsQueryVariables,
  WorkItemReviewState,
  WorkItemType,
} from "@digits-graphql/frontend/graphql-bearer"
import useSession from "@digits-shared/hooks/useSession"
import {
  OPEN_STATES,
  SelectedWorkItemState,
  TR_CLOSED_STATES,
  TransactionReviewTab,
} from "src/frontend/components/OS/Applications/Boost/WorkItems/hooks/boostTypes"
import type FrontendSession from "src/frontend/session"
import useIntervalOrigin from "src/shared/hooks/useIntervalOrigin"

export const TRANSACTION_REVIEW_TYPES = [WorkItemType.UnassignedCategory]

export function useListTransactionReviewItemsQueryVariables(
  state: SelectedWorkItemState,
  selectedTab?: TransactionReviewTab,
  itemType?: WorkItemType
): ListWorkItemsQueryVariables {
  const intervalOrigin = useIntervalOrigin()
  const { currentLegalEntityId: legalEntityId } = useSession<FrontendSession>()

  const types = React.useMemo(() => {
    if (itemType) return [itemType]

    return TRANSACTION_REVIEW_TYPES
  }, [itemType])

  const itemStates = React.useMemo(() => {
    switch (state) {
      case SelectedWorkItemState.Open:
        return OPEN_STATES
      case SelectedWorkItemState.Closed:
        return TR_CLOSED_STATES
    }
  }, [state])

  const reviewStates = React.useMemo(() => {
    switch (state) {
      case SelectedWorkItemState.Open:
        switch (selectedTab) {
          case TransactionReviewTab.PendingClientResponse:
            return [WorkItemReviewState.PendingClientResponse]
          case TransactionReviewTab.ClientResponded:
            return [WorkItemReviewState.ClientResponded]
          case TransactionReviewTab.TransactionReview:
            return undefined
        }
        return undefined
      case SelectedWorkItemState.Closed:
        return undefined
    }
  }, [selectedTab, state])

  const excludedReviewStates = React.useMemo(() => {
    switch (selectedTab) {
      case TransactionReviewTab.PendingClientResponse:
        return undefined
      case TransactionReviewTab.ClientResponded:
        return undefined

      case TransactionReviewTab.TransactionReview:
        switch (state) {
          case SelectedWorkItemState.Open:
            return [WorkItemReviewState.PendingClientResponse, WorkItemReviewState.ClientResponded]
          case SelectedWorkItemState.Closed:
            return undefined
        }
        return undefined
      default:
        return undefined
    }
  }, [selectedTab, state])

  return React.useMemo(
    () => ({
      intervalOrigin: {
        ...intervalOrigin,
        intervalCount: 1,
      },
      filter: {
        legalEntityId,
        itemStates,
        types,
        reviewStates,
        excludedReviewStates,
      },
      direction: DirectionFromOrigin.Past,
    }),
    [excludedReviewStates, intervalOrigin, itemStates, legalEntityId, reviewStates, types]
  )
}
